import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useLayoutEffect,
  memo,
} from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { animate, motion, AnimatePresence } from "framer-motion";
import {
  CAMERA_SCALE_MIN,
  CAMERA_SCALE_MAX,
  getDefaultCamera,
  clampCamera,
  zoomCameraAroundSvgPoint,
  clientPointToSvgUser,
} from "./worldMap/utils/cameraUtils";
import {
  MARKERS,
  MARKER_NAMES_SET,
  countryLabel,
  canonicalMarkerNameFromTopo,
  countryMeta,
  isInteractiveGeo,
  isGeoSelected,
  markerMatchesSelection,
  geoCenterLngLat,
} from "./worldMap/utils/geoUtils";

// =============================================================================
// Map data, markers, and topo → marker name matching
// =============================================================================

/** TopoJSON: Natural Earth 110m — jsDelivr */
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const MARKERS_BY_NAME = new Map(MARKERS.map((m) => [m.name, m]));

const SMALL_COUNTRIES = new Set(["Israel"]);

/** Shorter on-map labels (readability / less overlap). Tooltip & `aria-label` use full `MARKERS[].name`. */
const MARKER_DISPLAY_LABEL = {
  "United States of America": "United States",
};

function markerDisplayLabel(markerName) {
  return MARKER_DISPLAY_LABEL[markerName] ?? markerName;
}

/** SVG pill width from label length (map viewBox units, ~px at 960-wide map). */
function labelBadgeWidth(text, pad = 28) {
  const w = Math.ceil(text.length * 6.4 + pad);
  return Math.min(168, Math.max(76, w));
}

/** react-simple-maps default viewBox when width/height omitted */
const RSM_VIEW_W = 800;
const RSM_VIEW_H = 600;

// =============================================================================
// Theme & map styling tokens (colors, transitions, label metrics)
// =============================================================================

const AQUA = "#5eb8c4";
const NAVY_PAGE = "#060d1a";
const LAND_DIM = "#0f1624";
const LAND_NEUTRAL = "#1a2436";
const STROKE_DIM = "#1e2838";
const HOVER_FILL = "#1a2638";
const HOVER_STROKE = "rgba(94, 184, 196, 0.28)";
const T_MS = "0.25s";
const MARKER_TRANSITION = { duration: 0.25, ease: "easeOut" };

const LABEL_BADGE_H = 32;
const LABEL_BADGE_RX = 16;
const LABEL_BADGE_STEM_GAP = 5;
const LABEL_FONT_SIZE = 11;

// =============================================================================
// Camera bounds, thresholds, defaults, and pure projection helpers
// =============================================================================

const MOBILE_MAX_WIDTH = 768;

const CAMERA_ZOOM_IN_MOBILE = 650;
const CAMERA_ZOOM_IN_DESKTOP = 320;

const DRAG_THRESHOLD_PX = 8;
/** Mobile: allow page scroll until finger moves this far (then map pan takes over). */
const MOBILE_PAN_THRESHOLD_PX = 14;

const TOOLTIP_EST_W = 272;
const TOOLTIP_EST_H = 40;
const TOOLTIP_PAD = 8;

function clampTooltipCss(left, top) {
  if (typeof window === "undefined") return { left, top };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const l = Math.min(Math.max(TOOLTIP_PAD, left), vw - TOOLTIP_EST_W - TOOLTIP_PAD);
  const t = Math.min(Math.max(TOOLTIP_PAD, top), vh - TOOLTIP_EST_H - TOOLTIP_PAD);
  return { left: l, top: t };
}

// =============================================================================
// Memoized country layers (dim base + interactive hit targets)
// =============================================================================

const WorldMapGeographyLayers = memo(function WorldMapGeographyLayers({
  geographies,
  geoByMarkerRef,
  focusDim,
  selectedCountry,
  onInteractiveEnter,
  onInteractiveMove,
  onInteractiveLeave,
  onCountryClick,
}) {
  const { dimGeos, hitGeos, geoByMarker } = useMemo(() => {
    const dim = [];
    const hit = [];
    const byMarker = new Map();
    for (let i = 0; i < geographies.length; i += 1) {
      const geo = geographies[i];
      const key = canonicalMarkerNameFromTopo(geo);
      if (MARKER_NAMES_SET.has(key)) {
        hit.push(geo);
        byMarker.set(key, geo);
      } else {
        dim.push(geo);
      }
    }
    return { dimGeos: dim, hitGeos: hit, geoByMarker: byMarker };
  }, [geographies]);

  useLayoutEffect(() => {
    geoByMarkerRef.current = geoByMarker;
  }, [geoByMarker, geoByMarkerRef]);

  const dimOp = focusDim ? 0.08 : 0.72;

  return (
    <>
      {dimGeos.map((geo) => (
        <Geography
          key={`dim-${geo.rsmKey}`}
          geography={geo}
          tabIndex={-1}
          style={{
            default: {
              fill: LAND_DIM,
              stroke: STROKE_DIM,
              strokeWidth: 0.45,
              outline: "none",
              cursor: "default",
              pointerEvents: "none",
              opacity: dimOp,
              transition: `fill ${T_MS} ease, opacity ${T_MS} ease`,
            },
            hover: {
              fill: LAND_DIM,
              stroke: STROKE_DIM,
              strokeWidth: 0.45,
              outline: "none",
              pointerEvents: "none",
              opacity: dimOp,
            },
            pressed: {
              fill: LAND_DIM,
              outline: "none",
              pointerEvents: "none",
            },
          }}
        />
      ))}
      {hitGeos.map((geo) => {
        const isSelected = isGeoSelected(geo, selectedCountry);
        return (
          <Geography
            key={`hit-${geo.rsmKey}`}
            geography={geo}
            tabIndex={0}
            onMouseEnter={(e) => onInteractiveEnter(geo, e)}
            onMouseMove={(e) => onInteractiveMove(geo, e)}
            onMouseLeave={onInteractiveLeave}
            onClick={(e) => onCountryClick(geo, e)}
            style={{
              default: {
                fill: isSelected ? AQUA : focusDim ? LAND_DIM : LAND_NEUTRAL,
                stroke: isSelected ? AQUA : STROKE_DIM,
                strokeWidth: isSelected ? 1.75 : 0.45,
                outline: "none",
                cursor: "pointer",
                pointerEvents: "auto",
                opacity: isSelected ? 1 : focusDim ? 0.32 : 0.72,
                filter: isSelected ? "url(#country-glow)" : "none",
                transform: isSelected ? "scale(1.01)" : "scale(1)",
                transformOrigin: "center",
                transformBox: "fill-box",
                transition: "all 0.25s ease",
              },
              hover: {
                fill: isSelected ? AQUA : "#22324a",
                stroke: isSelected ? AQUA : AQUA,
                strokeWidth: isSelected ? 1.85 : 1,
                outline: "none",
                cursor: "pointer",
                pointerEvents: "auto",
                opacity: 1,
                filter: "url(#country-glow)",
                transform: isSelected ? "scale(1.01)" : "scale(1)",
                transformOrigin: "center",
                transformBox: "fill-box",
                transition: "all 0.2s ease",
              },
              pressed: {
                fill: isSelected ? "#6ec8d4" : "#22324a",
                stroke: AQUA,
                strokeWidth: 2,
                outline: "none",
                pointerEvents: "auto",
                opacity: 1,
                filter: "url(#country-glow)",
                transform: "scale(1.02)",
                transformOrigin: "center",
                transformBox: "fill-box",
                transition: "all 0.15s ease",
              },
            }}
          />
        );
      })}
    </>
  );
});

WorldMapGeographyLayers.displayName = "WorldMapGeographyLayers";

const MemoMarker = memo(function MemoMarker({
  m,
  r,
  sel,
  hov,
  fillOpacity,
  strokeOpacity,
  glow,
  lineEndY,
  offsetY,
  badgeFilter,
  labelW,
  rectY,
  badgeH,
  badgeRx,
  fillUrl,
  strokeCol,
  textFill,
  labelFontSize,
  textCy,
  onClick,
  onEnter,
  onMove,
  onLeave,
  onKeyDown,
  children,
}) {
  return (
    <Marker
      coordinates={m.coordinates}
      tabIndex={0}
      role="button"
      aria-label={m.name}
      onClick={(e) => onClick(e, m.name)}
      onMouseEnter={(e) => onEnter(m.name, e)}
      onMouseMove={(e) => onMove(m.name, e)}
      onMouseLeave={onLeave}
      onKeyDown={(e) => onKeyDown(e, m.name)}
      style={{
        default: { outline: "none", cursor: "pointer" },
        hover: { outline: "none", cursor: "pointer" },
        pressed: { outline: "none", cursor: "pointer" },
      }}
    >
      <g style={{ pointerEvents: "auto" }}>
        <motion.circle
          r={r}
          fill={AQUA}
          fillOpacity={fillOpacity}
          stroke={AQUA}
          strokeOpacity={strokeOpacity}
          strokeWidth={sel ? 1.25 : hov ? 1 : 0.75}
          style={{ filter: glow }}
          transition={MARKER_TRANSITION}
        />
        <line
          x1={0}
          y1={-r}
          x2={0}
          y2={lineEndY}
          stroke={AQUA}
          strokeOpacity={sel ? 0.42 : hov ? 0.34 : 0.22}
          strokeWidth={0.55}
          strokeLinecap="round"
          pointerEvents="none"
        />
        <g transform={`translate(0, ${offsetY})`}>
          <motion.g
            style={{
              pointerEvents: "auto",
              cursor: "pointer",
              filter: badgeFilter,
              transformOrigin: `0px ${textCy}px`,
              opacity: 1,
            }}
            animate={{ scale: hov || sel ? 1.05 : 1 }}
            transition={MARKER_TRANSITION}
          >
            <rect
              x={-labelW / 2}
              y={rectY}
              width={labelW}
              height={badgeH}
              rx={badgeRx}
              ry={badgeRx}
              fill={fillUrl}
              stroke={strokeCol}
              strokeWidth={sel ? 1.05 : 0.85}
              style={{
                transition: "stroke 0.25s ease, stroke-width 0.25s ease",
              }}
            />
            <text
              x={0}
              y={textCy}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={textFill}
              fontSize={labelFontSize}
              fontWeight={600}
              fontFamily="Rajdhani, system-ui, sans-serif"
              letterSpacing="0.04em"
              style={{
                pointerEvents: "none",
                userSelect: "none",
                transition: "fill 0.25s ease",
              }}
            >
              {children}
            </text>
          </motion.g>
        </g>
      </g>
    </Marker>
  );
});

MemoMarker.displayName = "MemoMarker";

// =============================================================================
// WorldMap (orchestration: state, gestures, camera, markers, tooltip)
// =============================================================================

export default function WorldMap() {
  const [geoObject, setGeoObject] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, text: "", x: 0, y: 0 });
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const geoByMarkerRef = useRef(new Map());
  const mapSvgRef = useRef(null);
  const mapInteractionRef = useRef(null);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < MOBILE_MAX_WIDTH
  );
  const [camera, setCamera] = useState(() =>
    getDefaultCamera(typeof window !== "undefined" && window.innerWidth < MOBILE_MAX_WIDTH)
  );
  const cameraRef = useRef(camera);
  const isMobileRef = useRef(isMobile);
  const cameraAnimIdRef = useRef(0);
  const cameraAnimCtrlRef = useRef(null);
  const panStateRef = useRef(null);
  const pointersMapRef = useRef(new Map());
  const pinchStateRef = useRef(null);
  const mapListenersAttachedRef = useRef(false);
  const lastTapRef = useRef(0);
  const suppressClickRef = useRef(false);
  const inertiaRafRef = useRef(null);
  const [zoomModifierDown, setZoomModifierDown] = useState(false);
  const zoomModifierDownRef = useRef(false);
  const hoverDelayTimeoutRef = useRef(null);
  const tooltipRafRef = useRef(null);
  const pendingTooltipRef = useRef(null);

  const [drag, setDrag] = useState({
    isDragging: false,
    hasMoved: false,
    startX: 0,
    startY: 0,
    startCenter: [0, 12],
  });

  useEffect(() => {
    cameraRef.current = camera;
  }, [camera]);

  // Desktop: show zoom cursor only when Ctrl/Cmd is pressed
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onKey = (e) => {
      const down = !!(e.ctrlKey || e.metaKey);
      if (zoomModifierDownRef.current === down) return;
      zoomModifierDownRef.current = down;
      setZoomModifierDown(down);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    window.addEventListener("blur", () => {
      zoomModifierDownRef.current = false;
      setZoomModifierDown(false);
    });
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
    };
  }, []);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => setIsMobile(window.innerWidth < MOBILE_MAX_WIDTH);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    cameraAnimCtrlRef.current?.stop();
    cameraAnimIdRef.current += 1;
    setCamera(getDefaultCamera(isMobile));
  }, [isMobile]);

  const stopInertia = useCallback(() => {
    if (inertiaRafRef.current != null) {
      cancelAnimationFrame(inertiaRafRef.current);
      inertiaRafRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    fetch(GEO_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`Map data failed (${res.status})`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setGeoObject(data);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message || "Could not load map");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // --- Interactive country hover tooltip ---

  const flushTooltip = useCallback(() => {
    if (tooltipRafRef.current != null) {
      cancelAnimationFrame(tooltipRafRef.current);
      tooltipRafRef.current = null;
    }
    const next = pendingTooltipRef.current;
    if (!next) return;
    pendingTooltipRef.current = null;
    setTooltip(next);
  }, []);

  const scheduleTooltipUpdate = useCallback(
    (next) => {
      pendingTooltipRef.current = next;
      if (tooltipRafRef.current != null) return;
      tooltipRafRef.current = requestAnimationFrame(() => {
        tooltipRafRef.current = null;
        flushTooltip();
      });
    },
    [flushTooltip]
  );

  const clearHoverDelay = useCallback(() => {
    if (hoverDelayTimeoutRef.current != null) {
      window.clearTimeout(hoverDelayTimeoutRef.current);
      hoverDelayTimeoutRef.current = null;
    }
  }, []);

  const handleInteractiveEnter = useCallback(
    (geo, evt) => {
      if (!isInteractiveGeo(geo)) return;
      clearHoverDelay();
      const next = {
        show: true,
        text: countryLabel(geo),
        x: evt.clientX,
        y: evt.clientY,
      };
      // Small hover delay to reduce jitter during fast mouse moves
      hoverDelayTimeoutRef.current = window.setTimeout(() => {
        scheduleTooltipUpdate(next);
      }, 40);
    },
    [clearHoverDelay, scheduleTooltipUpdate]
  );

  const handleInteractiveMove = useCallback(
    (geo, evt) => {
      if (!isInteractiveGeo(geo)) return;
      // Debounce tooltip updates to animation frames (~16ms)
      scheduleTooltipUpdate({
        show: true,
        text: countryLabel(geo),
        x: evt.clientX,
        y: evt.clientY,
      });
    },
    [scheduleTooltipUpdate]
  );

  const handleInteractiveLeave = useCallback(() => {
    clearHoverDelay();
    pendingTooltipRef.current = null;
    if (tooltipRafRef.current != null) {
      cancelAnimationFrame(tooltipRafRef.current);
      tooltipRafRef.current = null;
    }
    setTooltip((t) => ({ ...t, show: false }));
  }, [clearHoverDelay]);

  // --- Camera: spring interpolation & fly-to ---

  const startCameraAnim = useCallback((from, to) => {
    cameraAnimCtrlRef.current?.stop();
    const runId = ++cameraAnimIdRef.current;
    const fromSnap = clampCamera({ scale: from.scale, center: [...from.center] });
    const toSnap = clampCamera({ scale: to.scale, center: [...to.center] });
    cameraAnimCtrlRef.current = animate(0, 1, {
      type: "spring",
      stiffness: 70,
      damping: 20,
      mass: 1,
      onUpdate: (latest) => {
        if (runId !== cameraAnimIdRef.current) return;
        const t = typeof latest === "number" ? Math.min(1, Math.max(0, latest)) : 0;
        setCamera(
          clampCamera({
            scale: fromSnap.scale + (toSnap.scale - fromSnap.scale) * t,
            center: [
              fromSnap.center[0] + (toSnap.center[0] - fromSnap.center[0]) * t,
              fromSnap.center[1] + (toSnap.center[1] - fromSnap.center[1]) * t,
            ],
          })
        );
      },
    });
  }, []);

  const resetCameraAnim = useCallback(() => {
    const from = {
      scale: cameraRef.current.scale,
      center: [cameraRef.current.center[0], cameraRef.current.center[1]],
    };
    startCameraAnim(from, getDefaultCamera(isMobileRef.current));
  }, [startCameraAnim]);

  const flyCameraTo = useCallback(
    (lng, lat) => {
      const from = {
        scale: cameraRef.current.scale,
        center: [cameraRef.current.center[0], cameraRef.current.center[1]],
      };
      const mobile = isMobileRef.current;
      startCameraAnim(from, {
        scale: mobile ? CAMERA_ZOOM_IN_MOBILE : CAMERA_ZOOM_IN_DESKTOP,
        center: [lng, lat],
      });
    },
    [startCameraAnim]
  );

  // --- Pan release: inertial drift ---

  const runInertia = useCallback((velX, velY) => {
    const panMul = isMobileRef.current ? 0.2 : 0.1;
    const clampV = (v) => Math.min(20, Math.max(-20, v));
    let vx = clampV(-velX * 0.15 * panMul);
    let vy = clampV(velY * 0.15 * panMul);
    const friction = 0.94;

    const step = () => {
      vx = clampV(vx * friction);
      vy = clampV(vy * friction);
      if (Math.abs(vx) <= 0.1 && Math.abs(vy) <= 0.1) {
        inertiaRafRef.current = null;
        return;
      }
      setCamera((prev) =>
        clampCamera({
          ...prev,
          center: [prev.center[0] - vx, prev.center[1] + vy],
        })
      );
      inertiaRafRef.current = requestAnimationFrame(step);
    };

    if (Math.abs(vx) > 0.1 || Math.abs(vy) > 0.1) {
      stopInertia();
      inertiaRafRef.current = requestAnimationFrame(step);
    }
  }, [stopInertia]);

  const runInertiaRef = useRef(runInertia);
  runInertiaRef.current = runInertia;

  const mapPointerMoveListenerRef = useRef(null);
  const mapPointerEndListenerRef = useRef(null);

  // --- Window-level pointer listeners (pan threshold, pinch); stable ref instances ---

  if (mapPointerMoveListenerRef.current == null) {
    mapPointerMoveListenerRef.current = (e) => {
      const m = pointersMapRef.current;
      if (!m.has(e.pointerId)) return;

      m.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (m.size >= 2) {
        e.preventDefault();
        const pts = [...m.values()];
        if (pts.length < 2) return;
        const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
        let pinch = pinchStateRef.current;
        if (!pinch) {
          pinch = {
            startDist: dist,
            startScale: cameraRef.current.scale,
          };
          pinchStateRef.current = pinch;
        }
        if (Math.abs(dist - pinch.startDist) < 2) {
          return;
        }
        const scaleFactor = dist / pinch.startDist;
        const newScale = pinch.startScale * scaleFactor;
        const dampedScale = pinch.startScale + (newScale - pinch.startScale) * 0.15;
        const clampedScale = Math.min(CAMERA_SCALE_MAX, Math.max(CAMERA_SCALE_MIN, dampedScale));
        const prev = cameraRef.current;
        const svg = mapSvgRef.current;
        const midCx = (pts[0].x + pts[1].x) / 2;
        const midCy = (pts[0].y + pts[1].y) / 2;
        const svgPt = svg ? clientPointToSvgUser(svg, midCx, midCy) : null;
        if (svgPt) {
          setCamera(zoomCameraAroundSvgPoint(prev, svgPt, clampedScale, RSM_VIEW_W, RSM_VIEW_H));
        } else {
          setCamera(clampCamera({ scale: clampedScale, center: [...prev.center] }));
        }
        return;
      }

      const st = panStateRef.current;
      if (!st || e.pointerId !== st.pointerId) return;

      const panMul = isMobileRef.current ? 0.2 : 0.1;
      const dx = e.clientX - st.startX;
      const dy = e.clientY - st.startY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const mobile = isMobileRef.current;

      if (mobile) {
        // Mobile intent detection:
        // - vertical gesture should scroll the page (do nothing)
        // - only activate map pan for horizontal/intentional drags
        if (!st.dragging) {
          if (Math.abs(dy) > Math.abs(dx)) return;
          if (distance < MOBILE_PAN_THRESHOLD_PX) return;
          st.dragging = true;
          setDrag((prev) => ({ ...prev, isDragging: true, hasMoved: true }));
        }
      } else {
        const thr = DRAG_THRESHOLD_PX;
        if (!st.dragging && dx * dx + dy * dy > thr * thr) {
          st.dragging = true;
          setDrag((prev) => ({ ...prev, isDragging: true, hasMoved: true }));
        }
      }

      if (!st.dragging) return;

      e.preventDefault();
      st.velX = e.clientX - st.lastX;
      st.velY = e.clientY - st.lastY;
      st.lastX = e.clientX;
      st.lastY = e.clientY;
      setCamera(
        clampCamera({
          scale: cameraRef.current.scale,
          center: [
            st.startCenter[0] - dx * panMul,
            st.startCenter[1] + dy * panMul,
          ],
        })
      );
    };
  }

  if (mapPointerEndListenerRef.current == null) {
    mapPointerEndListenerRef.current = (e) => {
      const m = pointersMapRef.current;
      if (!m.has(e.pointerId)) return;

      m.delete(e.pointerId);

      const moveFn = mapPointerMoveListenerRef.current;
      const endFn = mapPointerEndListenerRef.current;

      if (m.size === 0) {
        window.removeEventListener("pointermove", moveFn);
        window.removeEventListener("pointerup", endFn);
        window.removeEventListener("pointercancel", endFn);
        mapListenersAttachedRef.current = false;

        const st = panStateRef.current;
        panStateRef.current = null;
        pinchStateRef.current = null;

        if (st?.dragging) {
          suppressClickRef.current = true;
          window.setTimeout(() => {
            suppressClickRef.current = false;
          }, 320);
          setDrag((prev) => ({
            ...prev,
            isDragging: false,
            hasMoved: false,
          }));
          runInertiaRef.current(st.velX, st.velY);
        } else {
          setDrag((prev) => ({
            ...prev,
            isDragging: false,
            hasMoved: false,
          }));
        }
        return;
      }

      pinchStateRef.current = null;
      const pid = m.keys().next().value;
      const pt = m.get(pid);
      if (pid != null && pt) {
        const startCenter = [...cameraRef.current.center];
        panStateRef.current = {
          pointerId: pid,
          startX: pt.x,
          startY: pt.y,
          startCenter,
          lastX: pt.x,
          lastY: pt.y,
          velX: 0,
          velY: 0,
          dragging: false,
        };
        setDrag({
          isDragging: false,
          hasMoved: false,
          startX: pt.x,
          startY: pt.y,
          startCenter,
        });
      }
    };
  }

  useEffect(
    () => () => {
      cameraAnimCtrlRef.current?.stop();
      if (inertiaRafRef.current != null) {
        cancelAnimationFrame(inertiaRafRef.current);
        inertiaRafRef.current = null;
      }
      if (mapListenersAttachedRef.current) {
        const moveFn = mapPointerMoveListenerRef.current;
        const endFn = mapPointerEndListenerRef.current;
        if (moveFn && endFn) {
          window.removeEventListener("pointermove", moveFn);
          window.removeEventListener("pointerup", endFn);
          window.removeEventListener("pointercancel", endFn);
        }
        mapListenersAttachedRef.current = false;
      }
      pointersMapRef.current.clear();
      panStateRef.current = null;
      pinchStateRef.current = null;
    },
    []
  );

  const onMapPointerDown = useCallback(
    (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;

      const m = pointersMapRef.current;
      const wasEmpty = m.size === 0;

      if (wasEmpty && isMobileRef.current && e.pointerType === "touch") {
        const now = Date.now();
        if (lastTapRef.current > 0 && now - lastTapRef.current < 300) {
          lastTapRef.current = now;
          stopInertia();
          cameraAnimCtrlRef.current?.stop();
          setSelectedCountry(null);
          resetCameraAnim();
          return;
        }
        lastTapRef.current = now;
      }

      stopInertia();
      cameraAnimCtrlRef.current?.stop();
      cameraAnimIdRef.current += 1;

      m.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (m.size >= 2) {
        panStateRef.current = null;
        const pts = [...m.values()];
        const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
        pinchStateRef.current = {
          startDist: dist,
          startScale: cameraRef.current.scale,
        };
        setDrag((prev) => ({ ...prev, isDragging: false, hasMoved: false }));
      } else {
        pinchStateRef.current = null;
        const { clientX, clientY } = e;
        const startCenter = [...cameraRef.current.center];
        panStateRef.current = {
          pointerId: e.pointerId,
          startX: clientX,
          startY: clientY,
          startCenter,
          lastX: clientX,
          lastY: clientY,
          velX: 0,
          velY: 0,
          dragging: false,
        };
        setDrag({
          isDragging: false,
          hasMoved: false,
          startX: clientX,
          startY: clientY,
          startCenter,
        });
      }

      if (!mapListenersAttachedRef.current) {
        mapListenersAttachedRef.current = true;
        const moveFn = mapPointerMoveListenerRef.current;
        const endFn = mapPointerEndListenerRef.current;
        window.addEventListener("pointermove", moveFn, { passive: false });
        window.addEventListener("pointerup", endFn);
        window.addEventListener("pointercancel", endFn);
      }
    },
    [stopInertia, resetCameraAnim]
  );

  useEffect(() => {
    if (!geoObject) return;
    const el = mapInteractionRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (isMobileRef.current) return;
      // Desktop: allow normal page scroll unless user holds Ctrl/Cmd (intentional zoom)
      if (!e.ctrlKey && !e.metaKey) return;
      const svg = mapSvgRef.current;
      if (!svg) return;
      const svgPt = clientPointToSvgUser(svg, e.clientX, e.clientY);
      if (!svgPt) return;
      e.preventDefault();
      e.stopPropagation();
      stopInertia();
      cameraAnimCtrlRef.current?.stop();
      cameraAnimIdRef.current += 1;
      const zoomFactor = 1.06;
      const factor = e.deltaY < 0 ? zoomFactor : 1 / zoomFactor;
      const prev = cameraRef.current;
      const nextScale = prev.scale * factor;
      setCamera(zoomCameraAroundSvgPoint(prev, svgPt, nextScale, RSM_VIEW_W, RSM_VIEW_H));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [geoObject, stopInertia]);

  const handleCountryClick = useCallback(
    (geo, evt) => {
      if (!isInteractiveGeo(geo)) return;
      if (suppressClickRef.current) return;
      evt.preventDefault();
      evt.stopPropagation();
      if (isGeoSelected(geo, selectedCountry)) {
        setTooltip((t) => ({ ...t, show: false }));
        setSelectedCountry(null);
        resetCameraAnim();
        return;
      }
      setTooltip((t) => ({ ...t, show: false }));
      setSelectedCountry(countryMeta(geo));
      const [lng, lat] = geoCenterLngLat(geo);
      flyCameraTo(lng, lat);
    },
    [selectedCountry, resetCameraAnim, flyCameraTo]
  );

  const handleMarkerClick = useCallback(
    (evt, markerName) => {
      if (suppressClickRef.current) return;
      evt.preventDefault();
      evt.stopPropagation();
      const geo = geoByMarkerRef.current.get(markerName);
      if (!geo) return;
      if (isGeoSelected(geo, selectedCountry)) {
        setTooltip((t) => ({ ...t, show: false }));
        setSelectedCountry(null);
        resetCameraAnim();
        return;
      }
      setTooltip((t) => ({ ...t, show: false }));
      setSelectedCountry(countryMeta(geo));
      const m = MARKERS_BY_NAME.get(markerName);
      const [lng, lat] =
        m && Array.isArray(m.coordinates) && m.coordinates.length >= 2
          ? m.coordinates
          : geoCenterLngLat(geo);
      flyCameraTo(lng, lat);
    },
    [selectedCountry, resetCameraAnim, flyCameraTo]
  );

  const handleMarkerEnter = useCallback(
    (markerName, evt) => {
      setHoveredMarker(markerName);
      clearHoverDelay();
      const next = {
        show: true,
        text: markerName,
        x: evt.clientX,
        y: evt.clientY,
      };
      hoverDelayTimeoutRef.current = window.setTimeout(() => {
        scheduleTooltipUpdate(next);
      }, 40);
    },
    [clearHoverDelay, scheduleTooltipUpdate]
  );

  const handleMarkerMove = useCallback(
    (markerName, evt) => {
      scheduleTooltipUpdate({
        show: true,
        text: markerName,
        x: evt.clientX,
        y: evt.clientY,
      });
    },
    [scheduleTooltipUpdate]
  );

  const handleMarkerLeave = useCallback(() => {
    setHoveredMarker(null);
    handleInteractiveLeave();
  }, [handleInteractiveLeave]);

  const onMarkerKeyDown = useCallback(
    (evt, markerName) => {
      if (evt.key === "Enter" || evt.key === " ") {
        evt.preventDefault();
        handleMarkerClick(evt, markerName);
      }
    },
    [handleMarkerClick]
  );

  const mapFrameClass = "relative block h-[85vh] w-full shrink-0 sm:h-[520px]";
  const mapFrameStyle = {
    minHeight: "70vh",
    display: "block",
    flexShrink: 0,
  };

  const focusDim = Boolean(selectedCountry);

  const handleResetView = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      stopInertia();
      setSelectedCountry(null);
      resetCameraAnim();
    },
    [resetCameraAnim, stopInertia]
  );

  const tooltipPos =
    tooltip.show && tooltip.text
      ? clampTooltipCss(tooltip.x + 12, tooltip.y - 40)
      : { left: 0, top: 0 };

  return (
    <div
      className="mx-auto flex w-full max-w-5xl shrink-0 flex-col"
      style={{
        background: NAVY_PAGE,
        ...(isMobile ? { flexShrink: 0, minHeight: "85vh" } : {}),
      }}
    >
      <div
        className="relative flex w-full shrink-0 flex-col items-center"
        style={{
          borderRadius: 14,
          background:
            "linear-gradient(180deg, #0a111d 0%, #060d1a 100%), radial-gradient(ellipse 80% 60% at 50% 40%, rgba(94, 184, 196, 0.05) 0%, transparent 60%)",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
          flexShrink: 0,
          ...(isMobile ? { minHeight: "85vh" } : {}),
        }}
      >
        <div
          className="relative w-full shrink-0 px-2 pb-2 pt-4 sm:px-4 sm:pb-4 sm:pt-6"
          style={isMobile ? { flexShrink: 0, minHeight: "85vh" } : undefined}
        >
          {!geoObject && !loadError && (
            <div className={mapFrameClass} style={mapFrameStyle}>
              <div className="flex h-full w-full items-center justify-center text-sm font-medium text-white/35" style={{ fontFamily: "Rajdhani, system-ui, sans-serif" }}>
                Loading map…
              </div>
            </div>
          )}

          {loadError && (
            <div className={mapFrameClass} style={mapFrameStyle}>
              <div
                className="flex h-full w-full flex-col items-center justify-center gap-1 px-4 text-center text-sm text-rose-300/80"
                style={{ fontFamily: "Rajdhani, system-ui, sans-serif" }}
              >
                <span>Could not load map data.</span>
                <span className="text-xs text-white/30">{loadError}</span>
              </div>
            </div>
          )}

          {geoObject && (
            <div className={mapFrameClass} style={mapFrameStyle}>
              <div
                ref={mapInteractionRef}
                className={`relative h-full w-full ${
                  drag.isDragging ? "cursor-grabbing" : "cursor-grab"
                }`}
                style={{
                  touchAction: isMobile ? "pan-y pinch-zoom" : "auto",
                  cursor: !isMobile && zoomModifierDown ? "zoom-in" : undefined,
                }}
                onPointerDown={onMapPointerDown}
                role="presentation"
              >
                <button
                  type="button"
                  onClick={handleResetView}
                  onPointerDown={(ev) => ev.stopPropagation()}
                  className="absolute right-1 top-1 z-20 rounded-md border border-white/[0.12] bg-[#0a111d]/90 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#c5dde3] shadow-md backdrop-blur-sm sm:right-2 sm:top-2 sm:px-3 sm:text-xs"
                  style={{ fontFamily: "Rajdhani, system-ui, sans-serif" }}
                >
                  Reset view
                </button>
        <ComposableMap
                  ref={mapSvgRef}
                  projection="geoEqualEarth"
          projectionConfig={{
                    scale: camera.scale,
                    center: camera.center,
                  }}
                  width={RSM_VIEW_W}
                  height={RSM_VIEW_H}
                  preserveAspectRatio="xMidYMid meet"
                  className="block h-full w-full sm:h-[520px]"
          style={{
            width: "100%",
                    height: "100%",
                    display: "block",
                    minHeight: 0,
            background: "transparent",
                  }}
                >
              <defs>
                    <filter id="country-glow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="3" result="blur1" />
                      <feGaussianBlur stdDeviation="6" result="blur2" />
                      <feMerge>
                        <feMergeNode in="blur2" />
                        <feMergeNode in="blur1" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                <linearGradient id="wm-badge-idle" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#1a2538" />
                  <stop offset="100%" stopColor="#0d1422" />
                </linearGradient>
                <linearGradient id="wm-badge-hover" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#1e3048" />
                  <stop offset="100%" stopColor="#121a2a" />
                </linearGradient>
                <linearGradient id="wm-badge-sel" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#1f4a55" />
                  <stop offset="100%" stopColor="#15353e" />
                </linearGradient>
              </defs>
              <Geographies geography={geoObject}>
                {({ geographies }) => (
                  <WorldMapGeographyLayers
                    geographies={geographies}
                    geoByMarkerRef={geoByMarkerRef}
                    focusDim={focusDim}
                    selectedCountry={selectedCountry}
                    onInteractiveEnter={handleInteractiveEnter}
                    onInteractiveMove={handleInteractiveMove}
                    onInteractiveLeave={handleInteractiveLeave}
                    onCountryClick={handleCountryClick}
                  />
                )}
              </Geographies>

              {MARKERS.map((m) => {
                const sel = markerMatchesSelection(m.name, selectedCountry);
                const hov = hoveredMarker === m.name;
                const isSmall = SMALL_COUNTRIES.has(m.name);
                const mScale = isMobile ? 1.26 : 1;
                const r0 = isSmall
                  ? sel
                    ? 7
                    : hov
                      ? 5.8
                      : 4.5
                  : sel
                    ? 5.2
                    : hov
                      ? 4.4
                      : 3.2;
                const r = r0 * mScale;
                const fillOpacity = sel ? 0.95 : hov ? 0.85 : 0.55;
                const strokeOpacity = sel ? 0.95 : hov ? 0.55 : 0.35;
                const glow =
                  hov && !sel
                    ? "drop-shadow(0 0 6px rgba(94, 184, 196, 0.35))"
                    : "none";
                const labelText = markerDisplayLabel(m.name);
                const badgeH = isSmall ? 36 : LABEL_BADGE_H;
                const badgeRx = isSmall ? 18 : LABEL_BADGE_RX;
                const labelFontSize = isSmall ? 12 : LABEL_FONT_SIZE;
                const labelPad = isSmall ? 34 : 28;
                const labelW = labelBadgeWidth(labelText, labelPad);
                const stemEndY = -r - LABEL_BADGE_STEM_GAP;
                const rectY = stemEndY - badgeH;
                const textCy = stemEndY - badgeH / 2;
                const offsetY = isSmall
                  ? isMobile
                    ? -34
                    : -52
                  : isMobile
                    ? -28
                    : -40;
                const lineEndY = stemEndY + offsetY;
                const fillUrl = sel ? "url(#wm-badge-sel)" : hov ? "url(#wm-badge-hover)" : "url(#wm-badge-idle)";
                const strokeCol = sel
                  ? "rgba(94, 184, 196, 0.58)"
                  : hov
                    ? "rgba(94, 184, 196, 0.36)"
                    : "rgba(255, 255, 255, 0.12)";
                const textFill = sel ? "#d4f4f8" : hov ? "#f0fafc" : "#e8f4f6";
                const badgeFilter = sel
                  ? "drop-shadow(0 2px 8px rgba(0,0,0,0.48)) drop-shadow(0 0 16px rgba(94,184,196,0.42))"
                  : hov
                    ? "drop-shadow(0 2px 10px rgba(0,0,0,0.42)) drop-shadow(0 0 12px rgba(94,184,196,0.3))"
                    : "drop-shadow(0 4px 14px rgba(0,0,0,0.4))";

                return (
                  <MemoMarker
                    key={m.name}
                    m={m}
                    r={r}
                    sel={sel}
                    hov={hov}
                    fillOpacity={fillOpacity}
                    strokeOpacity={strokeOpacity}
                    glow={glow}
                    lineEndY={lineEndY}
                    offsetY={offsetY}
                    badgeFilter={badgeFilter}
                    labelW={labelW}
                    rectY={rectY}
                    badgeH={badgeH}
                    badgeRx={badgeRx}
                    fillUrl={fillUrl}
                    strokeCol={strokeCol}
                    textFill={textFill}
                    labelFontSize={labelFontSize}
                    textCy={textCy}
                    onClick={handleMarkerClick}
                    onEnter={handleMarkerEnter}
                    onMove={handleMarkerMove}
                    onLeave={handleMarkerLeave}
                    onKeyDown={onMarkerKeyDown}
                  >
                    {labelText}
                  </MemoMarker>
                );
              })}
        </ComposableMap>
              </div>
            </div>
          )}

        <AnimatePresence>
            {tooltip.show && tooltip.text && (
            <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 2 }}
                transition={{ duration: 0.2 }}
                className="pointer-events-none fixed z-[100] rounded-md border px-3 py-1.5 text-xs font-semibold shadow-md"
              style={{
                  left: tooltipPos.left,
                  top: tooltipPos.top,
                  background: "rgba(10, 16, 28, 0.96)",
                  color: AQUA,
                  borderColor: "rgba(94, 184, 196, 0.35)",
                  fontFamily: "Rajdhani, system-ui, sans-serif",
                  boxShadow: "0 8px 28px rgba(0, 0, 0, 0.4)",
                  maxWidth: "min(260px, 88vw)",
                whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
              }}
            >
                {tooltip.text}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </div>

      <div className="mt-8 w-full max-w-lg self-center px-2">
        <AnimatePresence mode="wait">
          {selectedCountry ? (
            <motion.div
              key={selectedCountry.name}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              className="rounded-lg border border-white/[0.1] bg-white/[0.03] px-5 py-4 text-center"
            >
              <div
                className="mb-1 text-base font-semibold tracking-wide text-[#c5dde3]"
                style={{ fontFamily: "Rajdhani, system-ui, sans-serif" }}
              >
                {selectedCountry.name}
              </div>
              <div
                className="flex flex-wrap justify-center gap-x-5 gap-y-1.5 text-sm text-white/50"
                style={{ fontFamily: "Rajdhani, system-ui, sans-serif" }}
              >
                <InfoRow label="ISO" value={selectedCountry.iso} />
                {selectedCountry.region && <InfoRow label="Region" value={selectedCountry.region} />}
                {selectedCountry.subregion && (
                  <InfoRow label="Subregion" value={selectedCountry.subregion} />
                )}
              </div>
            </motion.div>
          ) : (
            <motion.p
              key="hint"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center text-xs text-white/35 sm:text-sm"
              style={{ fontFamily: "Rajdhani, system-ui, sans-serif" }}
            >
              Hover or click a marker or a highlighted country on the map for details.
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  if (value == null || value === "" || value === "—") return null;
  return (
    <span>
      <span className="font-semibold uppercase tracking-wider text-[#5eb8c4]/70">{label}: </span>
      <span className="text-white/70">{value}</span>
    </span>
  );
}
